/**
 * Top-of-dashboard banner stack that surfaces active announcements. Critical
 * announcements render in red with a short alert tone (Web Audio API beep)
 * played once per announcement (tracked via localStorage).
 *
 * Users can dismiss an announcement for their session — it disappears until
 * they refresh or the admin updates it.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, Info, ShieldAlert, X, Volume2 } from "lucide-react";
import { fmtDateTime } from "@/lib/dates";

const STYLE = {
  info: {
    wrap: "bg-blue-50 border-blue-200 text-blue-900",
    icon: Info,
    badge: "bg-blue-100 text-blue-900",
  },
  warning: {
    wrap: "bg-amber-50 border-amber-300 text-amber-900",
    icon: AlertTriangle,
    badge: "bg-amber-100 text-amber-900",
  },
  critical: {
    wrap: "bg-red-50 border-red-400 text-red-900",
    icon: ShieldAlert,
    badge: "bg-red-100 text-red-900 ring-1 ring-red-300",
  },
};

// Tiny Web-Audio beep — no audio asset needed.
function playAlertBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(660, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
    setTimeout(() => ctx.close(), 700);
  } catch { /* noop — autoplay may be blocked until first user interaction */ }
}

export default function AnnouncementsBanner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem("pulse-ann-dismissed") || "[]"));
    } catch { return new Set(); }
  });

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/announcements").then((r) => setItems(r.data || [])).catch(() => setItems([]));
    load();
    const t = setInterval(load, 2 * 60 * 1000); // refresh every 2 min
    return () => clearInterval(t);
  }, [user]);

  // Sound + visual ping for new critical announcements (once per id per device)
  useEffect(() => {
    items.filter((a) => a.severity === "critical").forEach((a) => {
      const key = `pulse-ann-played-${a.id}`;
      if (!localStorage.getItem(key)) {
        playAlertBeep();
        try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
      }
    });
  }, [items]);

  const visible = items.filter((a) => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try { sessionStorage.setItem("pulse-ann-dismissed", JSON.stringify([...next])); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-2" data-testid="announcements-banner">
      {visible.map((a) => {
        const s = STYLE[a.severity] || STYLE.info;
        const Icon = s.icon;
        return (
          <div
            key={a.id}
            className={`border rounded-sm px-3 py-2.5 flex items-start gap-3 ${s.wrap} ${a.severity === "critical" ? "animate-pulse-once" : ""}`}
            data-testid={`announcement-${a.id}`}
            role="alert"
          >
            <Icon size={16} className="shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider font-bold ${s.badge}`}>
                  {a.severity}
                </span>
                <span className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>{a.title}</span>
                {a.severity === "critical" && (
                  <Volume2 size={11} className="text-red-700" title="Sound alert played" />
                )}
                <span className="text-[10px] font-mono-data opacity-60 ml-auto truncate">
                  {a.created_by_name || "—"} · {fmtDateTime(a.created_at)}
                </span>
              </div>
              {a.body && (
                <div className="text-xs mt-1 whitespace-pre-wrap leading-relaxed">{a.body}</div>
              )}
            </div>
            <button
              onClick={() => dismiss(a.id)}
              className="shrink-0 opacity-60 hover:opacity-100 p-1 rounded-sm"
              aria-label="Dismiss"
              data-testid={`btn-dismiss-${a.id}`}
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
